import { Spinner, Stack } from '@servicetitan/design-system';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

interface InfiniteScrollProps {
    hasMore: boolean;
    next: () => Promise<any>;
    children: ReactNode;
    className?: string;
    loader?: ReactNode;
}

export const InfiniteScroll: FC<InfiniteScrollProps> = props => {
    const { children, className, hasMore, loader, next } = props;

    const isMounted = useRef<boolean>(false);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const loadData = async () => {
        try {
            setIsLoading(true);
            await next();
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const observerCallBack = useCallback((entries: IntersectionObserverEntry[]) => {
        if (isMounted.current) {
            const onlyEntry = entries[0];
            setIsIntersecting(onlyEntry.isIntersecting);
        }
    }, []);

    useEffect(() => {
        if (isIntersecting && hasMore && !isLoading) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIntersecting]);

    useEffect(() => {
        isMounted.current = true;
        const observer = new IntersectionObserver(observerCallBack, { rootMargin: '50px' });
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            isMounted.current = false;
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={className}>
            {children}
            <div ref={loadMoreRef} className={!hasMore ? 'd-n' : ''}>
                {loader ?? (
                    <Stack justifyContent="center" className="m-y-2">
                        <Spinner size="small" />
                    </Stack>
                )}
            </div>
        </div>
    );
};
